import { AccelFile, ChatMessage, ChatMessageType, GroupChatStore } from "@axl-dev/school-sdk";
import { PushpinFilled } from "@ant-design/icons";
import { Button, Empty, Modal, Spin, Tabs } from "antd";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useDrawer, useLoc, useSdk } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";
import { CommentFormEditor } from "@app/shared/ui/CommentForm/CommentFormEditor";
import { CommentFormHandler } from "@app/shared/ui/CommentForm/CommentForm";
import { sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";
import { Image } from "@app/shared/ui/Image";
import { Loc } from "@app/shared/ui/Loc";

import { GroupChatMessage } from "./components/GroupChatMessage/GroupChatMessage";

import styles from "./GroupChatView.module.scss";

export type GroupChatViewProps = {
  heightMode?: "fixed" | "fullscreen";
  height?: string;
  bgColor?: string;
  borderColor?: string;
  borderRadius?: string;
  conversationIds?: string[];
  showSidebar?: boolean;
  sidebarPosition?: "left" | "right";
  sidebarWidth?: string;
  sidebarBgColor?: string;
  sidebarTextColor?: string;
  itemHoverBgColor?: string;
  itemActiveBgColor?: string;
  itemBorderColor?: string;
  showHeader?: boolean;
  topicsBgColor?: string;
  topicColor?: string;
  topicActiveColor?: string;
  topicFontSize?: string;
  messagesBgColor?: string;
  bubbleInBgColor?: string;
  bubbleInTextColor?: string;
  bubbleOutBgColor?: string;
  bubbleOutTextColor?: string;
  bubbleBorderRadius?: string;
  bubbleFontSize?: string;
  messageFontFamily?: string;
  showMessageAuthor?: boolean;
  showMessageDate?: boolean;
  showMessageAvatar?: boolean;
  dayBgColor?: string;
  dayTextColor?: string;
  dayFontSize?: string;
  dayBorderRadius?: string;
  dayLineColor?: string;
  metaColor?: string;
  metaFontSize?: string;
  authorColor?: string;
  authorFontWeight?: string;
  badgeColor?: string;
  badgeTextColor?: string;
  composeBgColor?: string;
  composeInputBgColor?: string;
  composeIconColor?: string;
  composeIconSize?: string;
  composeTextColor?: string;
  composeFontSize?: string;
  composePlaceholderColor?: string;
  composeBorderColor?: string;
  composeBorderWidth?: string;
  composeBorderRadius?: string;
  composeBorderStyle?: string;
  borderWidth?: string;
  borderStyle?: string;
  fontFamily?: string;
  itemBorderWidth?: string;
  itemBorderStyle?: string;
  bubbleBorderColor?: string;
  bubbleBorderWidth?: string;
  bubbleBorderStyle?: string;
  dayBorderColor?: string;
  dayBorderWidth?: string;
  dayBorderStyle?: string;
  emptyTextColor?: string;
  conversationsText?: string;
  emptyConversationsText?: string;
  emptyMessagesText?: string;
  messagePlaceholderText?: string;
};

function useGroupChatStore(store: GroupChatStore) {
  useSyncExternalStore(
    (onStoreChange) => store.subscribe(onStoreChange),
    () => store.getSnapshot(),
  );
}

export const GroupChatView: React.FC<GroupChatViewProps> = (props) => {
  const sdk = useSdk();
  const store = useMemo(() => sdk.chat.createGroupChatStore(), [sdk]);
  useGroupChatStore(store);
  const loc = useLoc();
  const { user } = useUser();
  const formRef = useRef<CommentFormHandler | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const photoPreview = useDrawer<AccelFile>();
  const autoSelectedRef = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [fullscreenHeight, setFullscreenHeight] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const conversationId = searchParams.get("conversationId");
  const topicId = searchParams.get("topicId");
  const allowedIds = props.conversationIds;
  const pinnedConversationId = allowedIds?.length === 1 ? allowedIds[0] : null;
  const sidebarHidden = !!pinnedConversationId || props.showSidebar === false;
  const visibleConversations = useMemo(
    () =>
      allowedIds?.length
        ? store.conversations.filter((c) => allowedIds.includes(c.id))
        : store.conversations,
    [allowedIds, store.conversations],
  );

  useEffect(() => {
    if (props.heightMode !== "fullscreen") {
      setFullscreenHeight(null);
      return;
    }

    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const page = el.closest(".cabinet-page");
      const last = page?.lastElementChild;
      const footerHeight =
        last && !last.contains(el) ? last.getBoundingClientRect().height : 0;

      let bottomSpacing = 0;
      let node: HTMLElement | null = el;
      while (node && node !== page) {
        const parent: HTMLElement | null = node.parentElement;
        if (!parent) break;
        bottomSpacing += parseFloat(getComputedStyle(node).marginBottom) || 0;
        const parentStyle = getComputedStyle(parent);
        bottomSpacing += parseFloat(parentStyle.paddingBottom) || 0;
        bottomSpacing += parseFloat(parentStyle.borderBottomWidth) || 0;
        node = parent;
      }

      const available =
        window.innerHeight - rect.top - footerHeight - bottomSpacing;
      setFullscreenHeight(`${Math.round(Math.max(320, available))}px`);
    };

    measure();
    const frame = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
    };
  }, [props.heightMode]);

  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {};
    const set = (name: string, value?: string) => {
      const safe = sc(value ?? "");
      if (safe) vars[name] = safe;
    };
    set("--gc-height", fullscreenHeight ?? props.height);
    set("--gc-bg", props.bgColor);
    set("--gc-border-color", props.borderColor);
    set("--gc-border-radius", props.borderRadius);
    set("--gc-sidebar-width", props.sidebarWidth);
    set("--gc-sidebar-bg", props.sidebarBgColor);
    set("--gc-sidebar-text", props.sidebarTextColor);
    set("--gc-item-hover-bg", props.itemHoverBgColor);
    set("--gc-item-active-bg", props.itemActiveBgColor);
    set("--gc-item-border-color", props.itemBorderColor);
    set("--gc-topics-bg", props.topicsBgColor);
    set("--gc-topic-color", props.topicColor);
    set("--gc-topic-active-color", props.topicActiveColor);
    set("--gc-topic-font-size", props.topicFontSize);
    set("--gc-messages-bg", props.messagesBgColor);
    set("--gc-bubble-in-bg", props.bubbleInBgColor);
    set("--gc-bubble-in-color", props.bubbleInTextColor);
    set("--gc-bubble-out-bg", props.bubbleOutBgColor);
    set("--gc-bubble-out-color", props.bubbleOutTextColor);
    set("--gc-bubble-radius", props.bubbleBorderRadius);
    set("--gc-bubble-font-size", props.bubbleFontSize);
    set("--gc-day-bg", props.dayBgColor);
    set("--gc-day-color", props.dayTextColor);
    set("--gc-day-font-size", props.dayFontSize);
    set("--gc-day-radius", props.dayBorderRadius);
    set("--gc-day-line-color", props.dayLineColor);
    set("--gc-meta-color", props.metaColor);
    set("--gc-meta-font-size", props.metaFontSize);
    set("--gc-author-color", props.authorColor);
    set("--gc-author-font-weight", props.authorFontWeight);
    set("--gc-badge-bg", props.badgeColor);
    set("--gc-badge-text-color", props.badgeTextColor);
    set("--gc-compose-bg", props.composeBgColor);
    set("--gc-compose-input-bg", props.composeInputBgColor);
    set("--gc-compose-icon-color", props.composeIconColor);
    set("--gc-compose-icon-size", props.composeIconSize);
    set("--gc-compose-text-color", props.composeTextColor);
    set("--gc-compose-font-size", props.composeFontSize);
    set("--gc-compose-placeholder-color", props.composePlaceholderColor);
    set("--gc-compose-border-color", props.composeBorderColor);
    set("--gc-compose-border-width", props.composeBorderWidth);
    set("--gc-compose-border-radius", props.composeBorderRadius);
    set("--gc-compose-border-style", props.composeBorderStyle);
    set("--gc-border-width", props.borderWidth);
    set("--gc-border-style", props.borderStyle);
    set("--gc-font-family", props.fontFamily);
    set("--gc-message-font-family", props.messageFontFamily);
    set("--gc-item-border-width", props.itemBorderWidth);
    set("--gc-item-border-style", props.itemBorderStyle);
    set("--gc-bubble-border-color", props.bubbleBorderColor);
    set("--gc-bubble-border-width", props.bubbleBorderWidth);
    set("--gc-bubble-border-style", props.bubbleBorderStyle);
    set("--gc-day-border-color", props.dayBorderColor);
    set("--gc-day-border-width", props.dayBorderWidth);
    set("--gc-day-border-style", props.dayBorderStyle);
    set("--gc-empty-color", props.emptyTextColor);
    return vars as React.CSSProperties;
  }, [
    fullscreenHeight,
    props.height,
    props.bgColor,
    props.borderColor,
    props.borderRadius,
    props.sidebarWidth,
    props.sidebarBgColor,
    props.sidebarTextColor,
    props.itemHoverBgColor,
    props.itemActiveBgColor,
    props.itemBorderColor,
    props.topicsBgColor,
    props.topicColor,
    props.topicActiveColor,
    props.topicFontSize,
    props.messagesBgColor,
    props.bubbleInBgColor,
    props.bubbleInTextColor,
    props.bubbleOutBgColor,
    props.bubbleOutTextColor,
    props.bubbleBorderRadius,
    props.bubbleFontSize,
    props.dayBgColor,
    props.dayTextColor,
    props.dayFontSize,
    props.dayBorderRadius,
    props.dayLineColor,
    props.metaColor,
    props.metaFontSize,
    props.authorColor,
    props.authorFontWeight,
    props.badgeColor,
    props.badgeTextColor,
    props.composeBgColor,
    props.composeInputBgColor,
    props.composeIconColor,
    props.composeIconSize,
    props.composeTextColor,
    props.composeFontSize,
    props.composePlaceholderColor,
    props.composeBorderColor,
    props.composeBorderWidth,
    props.composeBorderRadius,
    props.composeBorderStyle,
    props.borderWidth,
    props.borderStyle,
    props.fontFamily,
    props.messageFontFamily,
    props.itemBorderWidth,
    props.itemBorderStyle,
    props.bubbleBorderColor,
    props.bubbleBorderWidth,
    props.bubbleBorderStyle,
    props.dayBorderColor,
    props.dayBorderWidth,
    props.dayBorderStyle,
    props.emptyTextColor,
  ]);


  useEffect(() => {
    store.init(pinnedConversationId ?? conversationId, topicId);
    return () => store.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, pinnedConversationId]);

  useEffect(() => {
    if (pinnedConversationId) return;
    if (!conversationId) return;
    void store.applyDeepLink(conversationId, topicId);
  }, [conversationId, pinnedConversationId, store, topicId, location.key, location.state]);

  useEffect(() => {
    if (!sidebarHidden) return;
    if (store.selectedConversation) return;
    const target = pinnedConversationId
      ? store.conversations.find((c) => c.id === pinnedConversationId)
      : visibleConversations[0];
    if (!target || autoSelectedRef.current === target.id) return;
    autoSelectedRef.current = target.id;
    void store.selectConversation(target);
  }, [sidebarHidden, pinnedConversationId, visibleConversations, store, store.conversations, store.selectedConversation]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void store.syncTail();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [store]);

  useEffect(() => {
    if (store.loadingMessages) return;
    if (!store.atBottom) return;
    let cancelled = false;
    const scrollToBottom = () => {
      const el = messagesRef.current;
      if (!el || cancelled) return;
      el.scrollTop = el.scrollHeight;
      store.setAtBottom(true);
      void store.syncTail();
    };
    const frame = requestAnimationFrame(() => {
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    });
    const timer = window.setTimeout(scrollToBottom, 80);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [
    store,
    store.loadingMessages,
    store.messages.length,
    store.selectedConversation?.id,
    store.selectedTopic?.id,
    store.atBottom,
  ]);

  useEffect(() => {
    if (!allowedIds?.length || pinnedConversationId) return;
    if (visibleConversations.length >= allowedIds.length) return;
    if (!store.hasMoreConversations || store.loadingMoreConversations) return;
    void store.loadMoreConversations();
  }, [
    allowedIds,
    pinnedConversationId,
    visibleConversations.length,
    store,
    store.hasMoreConversations,
    store.loadingMoreConversations,
  ]);

  const onSidebarScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80 && store.hasMoreConversations)
      void store.loadMoreConversations();
  }, [store]);

  const onMessagesScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop < 40 && store.hasMoreOlderMessages) void store.loadOlderMessages();
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    const wasAtBottom = store.atBottom;
    store.setAtBottom(nearBottom);
    if (nearBottom && !wasAtBottom) void store.syncTail();
  }, [store]);

  const onTopicChange = useCallback((key: string) => {
    const topic = store.topics.find((t) => t.id === key);
    if (topic) void store.selectTopic(topic);
  }, [store]);

  const onReply = useCallback((message: ChatMessage) => {
    store.setReplyMessage(message);
    formRef.current?.focus();
  }, [store]);

  const onPhotoClick = useCallback((file: AccelFile) => photoPreview.open(file), [photoPreview]);

  const send = useCallback(async (text: string, files: AccelFile[]) => {
    if (formRef.current?.hasUploadingFiles()) return false;
    return store.sendMessage(text, files);
  }, [store]);

  const studentReadOnly = !!store.selectedTopic?.isReadOnly;
  const composeDisabled =
    store.sending ||
    studentReadOnly ||
    (!!store.selectedConversation?.topicsEnabled && !store.selectedTopic);

  return (
    <div className={styles.root} style={cssVars} ref={rootRef}>
      <div className={cn(styles.layout, props.sidebarPosition === "right" && styles.layoutSidebarRight)}>
        {!sidebarHidden && (
          <aside
            className={styles.sidebar}
            onScroll={onSidebarScroll}
          >
            <div className={styles.sidebarTitle}>
              {props.conversationsText || (
                <Loc word="Group chat.conversations" default="Conversations" />
              )}
            </div>
            {store.loadingConversations && visibleConversations.length === 0 && (
              <div className="p-16">
                <Spin />
              </div>
            )}
            {visibleConversations.map((c) => (
              <div
                key={c.id}
                className={cn(
                  styles.conversationItem,
                  store.selectedConversation?.id === c.id && styles.conversationItemActive,
                )}
                onClick={() => store.selectConversation(c)}
              >
                <div className={styles.conversationItemBody}>
                  <div className="fw-500">{c.title || c.name}</div>
                  {c.memberCount > 0 && (
                    <div className="fs-12 color-secondary">
                      {loc.word("Group chat.membersCount", {
                        default: "{0} members",
                        args: [c.memberCount],
                      })}
                    </div>
                  )}
                </div>
                <div className={styles.conversationItemMeta}>
                  {c.isPinned === true && <PushpinFilled className={styles.pinIcon} />}
                  {c.unreadMessageCount > 0 && (
                    <div
                      className={cn(
                        styles.unreadBadge,
                        "no-select color-white fs-12 br-full flex justify-center align-center",
                      )}
                    >
                      {c.unreadMessageCount > 99 ? "99+" : c.unreadMessageCount}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {store.loadingMoreConversations && (
              <div className="flex justify-center p-8">
                <Spin size="small" />
              </div>
            )}
            {!store.loadingConversations && visibleConversations.length === 0 && (
              <div className={styles.empty}>
                {props.emptyConversationsText || (
                  <Loc word="Group chat.emptyConversations" default="No group chats yet" />
                )}
              </div>
            )}
          </aside>
        )}

        <section className={styles.main}>
          {!store.selectedConversation ? (
            <div className={styles.mainEmpty}>
              {store.loadingConversations ? (
                <Spin />
              ) : visibleConversations.length === 0 ? (
                <Empty
                  description={
                    props.emptyConversationsText ||
                    loc.word("Group chat.emptyConversations", { default: "No group chats yet" })
                  }
                />
              ) : (
                <Empty
                  description={loc.word("Group chat.selectConversation", {
                    default: "Select a conversation",
                  })}
                />
              )}
            </div>
          ) : (
            <>
              {props.showHeader !== false && (
                <div className={styles.header}>
                  <div className="fw-600 fs-16">{store.selectedConversation.title}</div>
                </div>
              )}

              {store.selectedConversation.topicsEnabled && (
                <div className={styles.topics}>
                  {store.loadingTopics ? (
                    <Spin size="small" />
                  ) : (
                    <Tabs
                      activeKey={store.selectedTopic?.id}
                      onChange={onTopicChange}
                      items={store.topics.map((t) => ({
                        key: t.id,
                        label: (
                          <span className={styles.topicLabel}>
                            <span>{t.title}</span>
                            {t.unreadMessageCount > 0 && (
                              <div
                                className={cn(
                                  styles.unreadBadge,
                                  "no-select color-white fs-12 br-full flex justify-center align-center",
                                )}
                              >
                                {t.unreadMessageCount > 99 ? "99+" : t.unreadMessageCount}
                              </div>
                            )}
                          </span>
                        ),
                      }))}
                    />
                  )}
                </div>
              )}

              <div
                className={styles.messages}
                ref={messagesRef}
                onScroll={onMessagesScroll}
              >
                {store.loadingMessages ? (
                  <div className="flex justify-center p-16">
                    <Spin />
                  </div>
                ) : store.messages.length === 0 ? (
                  <div className={styles.empty}>
                    {props.emptyMessagesText || (
                      <Loc word="Group chat.emptyMessages" default="No messages yet" />
                    )}
                  </div>
                ) : (
                  store.messages.map((m, index) => (
                    <GroupChatMessage
                      key={m.id || `${m.topicIndex ?? "t"}-${m.index ?? index}`}
                      message={m}
                      previousMessage={store.messages[index - 1]}
                      nextMessage={store.messages[index + 1]}
                      mine={m.author?.studentId === user?.id}
                      showAdminTag
                      showAuthor={props.showMessageAuthor !== false}
                      showDate={props.showMessageDate !== false}
                      showAvatar={props.showMessageAvatar !== false}
                      canReply={!composeDisabled}
                      onReply={onReply}
                      onPhotoClick={onPhotoClick}
                    />
                  ))
                )}
              </div>

              {photoPreview.visible && photoPreview.data && (
                <Modal
                  className="p-0"
                  open={photoPreview.visible}
                  width={800}
                  footer={null}
                  onCancel={() => photoPreview.close()}
                >
                  <Image file={photoPreview.data} fit="contain" width="100%" />
                </Modal>
              )}

              {studentReadOnly ? (
                <div className={styles.readonlyBanner}>
                  <Loc
                    word="Group chat.readOnlyHint"
                    default="This topic is read-only. Only administrators can send messages."
                  />
                </div>
              ) : (
                <div className={styles.compose}>
                  {store.replyMessage && (
                    <div className={styles.replyPreview}>
                      <div className="flex-1 overflow-hidden">
                        <div className="color-primary fs-12">
                          {store.replyMessage.author?.fullName ||
                            store.replyMessage.author?.email ||
                            "—"}
                        </div>
                        <div className="ellipsis fs-12">
                          {store.replyMessage.type === ChatMessageType.Voice
                            ? loc.word("Chat.voiceMessage", { default: "Voice message" })
                            : store.replyMessage.text ||
                              store.replyMessage.plainText ||
                              loc.word("Attachment", { default: "Attachment" })}
                        </div>
                      </div>
                      <Button type="text" size="small" onClick={() => store.setReplyMessage(null)}>
                        ×
                      </Button>
                    </div>
                  )}
                  <CommentFormEditor
                    handlerRef={formRef}
                    onSend={send}
                    placeholder={props.messagePlaceholderText || loc.word("Group chat.messagePlaceholder", {
                      default: "Write a message...",
                    })}
                    clearAfterSending
                    useEmoji
                    useFiles
                    useVoice
                    audioPlayerSize="middle"
                    disabled={composeDisabled}
                    enterToSubmit
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    editorClassName={styles.composeEditor}
                    inputClassName={styles.composeInput}
                    fileProps={{
                      draggable: true,
                      view: "list",
                      size: "md",
                    }}
                    borderless
                  />
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};
